import axios, { type AxiosInstance } from 'axios';
import { getCorrelationId } from './applicationInsights';
import {
  AUTH_SESSION_EXPIRED_CODE,
  csrfHeaders,
  notifyAuthSessionExpired,
} from './auth/session';
import { createLogger } from './logger';

interface CreateAxiosClientOptions {
  baseURL: string;
  loggerName: string;
}

let authSessionExpiryHandled = false;

export interface ApiValidationFieldError {
  field: string;
  message: string;
}

function apiErrorDetail(err: unknown): unknown {
  return (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
}

export function apiErrorStatus(err: unknown): number | null {
  const status = (err as { response?: { status?: unknown } })?.response?.status;
  return typeof status === 'number' ? status : null;
}

function apiValidationMessage(message: unknown): string | null {
  if (typeof message !== 'string') return null;
  const cleaned = message.replace(/^Value error,\s*/i, '').trim();
  return cleaned || null;
}

export function apiValidationFieldErrors(err: unknown): ApiValidationFieldError[] {
  const detail = apiErrorDetail(err);
  if (!Array.isArray(detail)) return [];

  return detail
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const loc = (item as { loc?: unknown }).loc;
      const message = apiValidationMessage((item as { msg?: unknown }).msg);
      if (!Array.isArray(loc) || !message) return null;

      const path = loc
        .filter((part): part is string | number => typeof part === 'string' || typeof part === 'number')
        .filter((part) => part !== 'body')
        .join('.');
      return path ? { field: path, message } : null;
    })
    .filter((error): error is ApiValidationFieldError => error !== null);
}

export function apiErrorMessage(err: unknown): string | null {
  const detail = apiErrorDetail(err);
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => apiValidationMessage((item as { msg?: unknown } | null)?.msg))
      .filter((message): message is string => Boolean(message));
    if (messages.length > 0) {
      return messages.join('; ');
    }
  }
  if (detail && typeof detail === 'object') {
    const errors = (detail as { errors?: unknown }).errors;
    if (Array.isArray(errors)) {
      const messages = errors.filter(
        (error): error is string => typeof error === 'string' && error.trim().length > 0
      );
      if (messages.length > 0) {
        return messages.join('; ');
      }
    }
    const message = (detail as { message?: unknown }).message;
    return typeof message === 'string' ? message : null;
  }
  const responseMessage = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (typeof responseMessage === 'string' && responseMessage.trim()) {
    return responseMessage;
  }
  const message = (err as { message?: unknown })?.message;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }
  return null;
}

export function apiErrorCode(err: unknown): string | null {
  const detail = apiErrorDetail(err);
  if (!detail || typeof detail !== 'object') return null;
  const code = (detail as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

function createAxiosClient({
  baseURL,
  loggerName,
}: CreateAxiosClientOptions): AxiosInstance {
  const logger = createLogger(loggerName);
  const client = axios.create({ baseURL, withCredentials: true });

  client.interceptors.request.use(async (config) => {
    config.headers['X-Correlation-ID'] = getCorrelationId();

    for (const [key, value] of Object.entries(csrfHeaders(config.method))) {
      config.headers[key] = value;
    }

    return config;
  });

  client.interceptors.response.use(
    response => {
      return response;
    },
    error => {
      // Aborted requests are intentional; don't surface them as errors.
      if (
        axios.isCancel(error) ||
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError'
      ) {
        return Promise.reject(error);
      }

      if (
        error.response?.status === 401 &&
        apiErrorCode(error) === AUTH_SESSION_EXPIRED_CODE
      ) {
        if (!authSessionExpiryHandled) {
          authSessionExpiryHandled = true;
          logger.info('Authentication session expired', {
            url: error.config?.url,
            method: error.config?.method,
          });
          notifyAuthSessionExpired();
        }
        return Promise.reject(error);
      }

      // Only log errors that aren't 404s (Not Found)
      // This helps keep the console clean when intentionally handling missing files
      if (error.response?.status !== 404) {
        logger.error('API Error', error, {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          data: error.response?.data,
        });
      } else {
        // For 404 errors, we can log a more discreet message or not log at all
        console.info('Resource not found:', error.config?.url);
      }

      return Promise.reject(error);
    }
  );

  return client;
}

let resolvedBackendClient: AxiosInstance | undefined;

function ensureBackendClient(): AxiosInstance {
  if (!resolvedBackendClient) {
    resolvedBackendClient = createAxiosClient({
      // Same-origin relative path. Front Door routes '/api/*' to the backend;
      // locally Vite proxies the same path to localhost:8000.
      baseURL: '/api',
      loggerName: 'backend-client',
    });
  }
  return resolvedBackendClient;
}

/**
 * Lazy proxy: defers axios instance creation until first method access.
 */
export const backendClient = new Proxy({} as AxiosInstance, {
  get(_target, prop) {
    const client = ensureBackendClient();
    const value = client[prop as keyof AxiosInstance];
    if (typeof value === 'function') {
      return (value as (...args: unknown[]) => unknown).bind(client);
    }
    return value;
  },
});
